import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

import type {
  ITenant,
  IDefaultFilter,
  IBillingProfile,
  IBillingProfileInput,
} from '../tenants.types';

type ApiResponse<T> = T | { data?: T };

export type TenantPermissionAllowanceMetadata = {
  allowed?: boolean;
  description?: string;
} | null;

export type TenantPermissionAllowance = {
  permissionId: string;
  code: string;
  source: string;
  sourceId: string | null;
  /** Effective allowlist flag; false when soft-revoked. */
  allowed: boolean;
  /** null on legacy rows means allowed. */
  metadata: TenantPermissionAllowanceMetadata;
};

/** Rows with metadata null are treated as allowed (API also sets `allowed`). */
export function isTenantPermissionEffectivelyAllowed(row: TenantPermissionAllowance): boolean {
  if (typeof row.allowed === 'boolean') return row.allowed;
  if (row.metadata == null) return true;
  return row.metadata?.allowed !== false;
}

export type TenantPermissionAllowancesData = {
  tenantId: string;
  allowances: TenantPermissionAllowance[];
};

const unwrapResponse = <T>(response: ApiResponse<T>, fallback: T): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data?: T }).data ?? fallback;
  }
  return (response as T) ?? fallback;
};

const TENANT_API = {
  list: 'api/tenants/list',
  add: 'api/tenants/add',
  update: 'api/tenants',
  details: 'api/tenants/details',
  delete: 'api/tenants/delete',
  limit: 'api/tenants/limit',
  modules: 'api/tenants/modules',
  moduleActions: 'api/tenants/module-actions',
  permissions: 'api/tenants/permissions',
  listModules: 'api/tenants/list-modules',
  listModuleActions: 'api/tenants/list-module-actions',
  listPermissions: 'api/tenants/list-permissions',
  billingProfile: 'api/tenants/billing-profile',
};

const getErrorStatus = (error: FetchBaseQueryError) =>
  'originalStatus' in error ? error.originalStatus : error.status;

type BillingProfileResponse = { data?: { billingProfile?: IBillingProfile | null } };

export type TenantPropagationEntry = {
  tenantId: string;
  tenantName: string;
  moduleCreated?: boolean;
  moduleActionCreated?: boolean;
  moduleResolved?: boolean;
  tenantPermissionId?: string;
  tenantPermissionCreated?: boolean;
  tenantDb?: {
    moduleCreated?: boolean;
    moduleActionCreated?: boolean;
    permissionCreated?: boolean;
    rolePermissionCreated?: boolean;
    adminRoleFound?: boolean;
    error?: string;
  };
  error?: string;
};

export type GlobalModuleRow = {
  id: string;
  name: string;
  description?: string | null;
};

export type GlobalModuleActionRow = {
  id: string;
  name: string;
  description?: string | null;
  moduleId: string;
  moduleName?: string;
};

export type GlobalPermissionRow = {
  id: string;
  code: string;
  label?: string;
  description?: string | null;
  scope?: string;
  moduleId?: string;
  moduleActionId?: string;
  moduleName?: string;
  moduleActionName?: string;
};

const extractArray = <T>(response: ApiResponse<unknown>, keys: string[]): T[] => {
  const raw = unwrapResponse(response, null as unknown);
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    for (const key of keys) {
      const value = (raw as Record<string, unknown>)[key];
      if (Array.isArray(value)) return value as T[];
    }
  }
  return [];
};

export const tenantApi = createApi({
  reducerPath: 'tenantApi',
  baseQuery,
  tagTypes: ['BillingProfile'],
  endpoints: (builder) => ({
    // ----------------------------------------------------------------------
    // NOTE FOR DEVELOPERS:
    // Please maintain the following order for endpoints ensuring CRUD consistency:
    // 1. List (Filter)
    // 2. Details
    // 3. Create
    // 4. Update
    // 5. Delete
    // ----------------------------------------------------------------------

    filterTenant: builder.mutation({
      query: (filterQuery: IDefaultFilter) => ({
        url: TENANT_API.list,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { tenant?: { list?: ITenant[]; total?: number } };
      }) => ({
        list: response?.data?.tenant?.list?.map((listItem) => ({ ...listItem })) ?? [],
        total: response?.data?.tenant?.total ?? 0,
      }),
    }),

    getTenantDtl: builder.mutation({
      query: (id: string) => ({
        url: `${TENANT_API.details}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    createTenant: builder.mutation<ITenant, Partial<ITenant>>({
      query: (newTenant) => ({
        url: TENANT_API.add,
        method: 'POST',
        body: newTenant,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { tenant: ITenant }; success: boolean }): ITenant =>
        response.success && response.data?.tenant ? response.data.tenant : ({} as ITenant),
    }),

    updateTenant: builder.mutation<ITenant, { id: string; updates: Partial<ITenant> }>({
      query: ({ id, updates }) => ({
        url: `${TENANT_API.update}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    deleteTenant: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `${TENANT_API.update}/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // Billing profile of the logged-in user's own tenant (tenant comes from the token).
    // The arg is the tenantId only so the cache is keyed per tenant.
    getBillingProfile: builder.query<IBillingProfile | null, string>({
      async queryFn(_tenantId, _api, _extraOptions, fetchWithBQ) {
        // TODO: switch to GET once the backend ships it. Until then, a PUT with an empty body
        // changes nothing and returns the saved profile, or 404 when there is none yet.
        const res = await fetchWithBQ({
          url: TENANT_API.billingProfile,
          method: 'PUT',
          body: {},
        });
        if (res.error) {
          if (getErrorStatus(res.error) === 404) return { data: null };
          return { error: res.error };
        }
        return { data: (res.data as BillingProfileResponse)?.data?.billingProfile ?? null };
      },
      providesTags: ['BillingProfile'],
    }),

    saveBillingProfile: builder.mutation<
      IBillingProfile | null,
      { tenantId: string; exists: boolean; values: IBillingProfileInput }
    >({
      async queryFn({ exists, values }, _api, _extraOptions, fetchWithBQ) {
        const send = (method: 'POST' | 'PUT') =>
          fetchWithBQ({ url: TENANT_API.billingProfile, method, body: values });

        let res = await send(exists ? 'PUT' : 'POST');
        // `exists` can be stale (e.g. saved from another tab), so retry with the other verb.
        if (res.error && getErrorStatus(res.error) === (exists ? 404 : 409)) {
          res = await send(exists ? 'POST' : 'PUT');
        }
        if (res.error) return { error: res.error };
        return { data: (res.data as BillingProfileResponse)?.data?.billingProfile ?? null };
      },
      async onQueryStarted({ tenantId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(tenantApi.util.upsertQueryData('getBillingProfile', tenantId, data));
        } catch {
          // Error is surfaced to the caller via unwrap().
        }
      },
    }),

    updateLimit: builder.mutation({
      query: (upateData) => ({
        url: TENANT_API.limit,
        method: 'POST',
        body: upateData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    getTenantPermissionAllowances: builder.query<TenantPermissionAllowancesData, { tenantId: string }>({
      query: ({ tenantId }) => ({
        url: `api/tenants/${tenantId}/permission-allowances`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<TenantPermissionAllowancesData>) => {
        const data = unwrapResponse(response, { tenantId: '', allowances: [] });
        return {
          tenantId: data.tenantId,
          allowances: Array.isArray(data.allowances)
            ? [...data.allowances].sort((a, b) => String(a.code).localeCompare(String(b.code)))
            : [],
        };
      },
    }),

    setTenantPermissionAllowance: builder.mutation<
      TenantPermissionAllowancesData,
      { tenantId: string; permission: string; allowed: boolean }
    >({
      query: ({ tenantId, permission, allowed }) => ({
        url: `api/tenants/${tenantId}/permission-allowances`,
        method: 'PUT',
        body: { permission, allowed },
      }),
      transformResponse: (response: ApiResponse<TenantPermissionAllowancesData>) =>
        unwrapResponse(response, { tenantId: '', allowances: [] }),
    }),

    listGlobalModules: builder.query<GlobalModuleRow[], void>({
      query: () => ({ url: TENANT_API.listModules, method: 'POST' }),
      transformResponse: (response: ApiResponse<unknown>) =>
        extractArray<GlobalModuleRow>(response, ['modules', 'module', 'list']),
    }),

    listGlobalModuleActions: builder.query<GlobalModuleActionRow[], void>({
      query: () => ({ url: TENANT_API.listModuleActions, method: 'POST' }),
      transformResponse: (response: ApiResponse<unknown>) =>
        extractArray<GlobalModuleActionRow>(response, ['moduleActions', 'moduleAction', 'list']),
    }),

    listGlobalPermissions: builder.query<GlobalPermissionRow[], void>({
      query: () => ({ url: TENANT_API.listPermissions, method: 'POST' }),
      transformResponse: (response: ApiResponse<unknown>) =>
        extractArray<GlobalPermissionRow>(response, ['permissions', 'permission', 'list']),
    }),

    createGlobalModule: builder.mutation<
      { module: GlobalModuleRow; tenantPropagation: TenantPropagationEntry[] },
      { name: string; description?: string }
    >({
      query: (body) => ({
        url: TENANT_API.modules,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<{
        module?: GlobalModuleRow;
        tenantPropagation?: TenantPropagationEntry[];
      }>) => {
        const data = unwrapResponse(response, {});
        return {
          module: data.module ?? ({ id: '', name: '' } as GlobalModuleRow),
          tenantPropagation: data.tenantPropagation ?? [],
        };
      },
    }),

    createGlobalModuleAction: builder.mutation<
      { moduleAction: GlobalModuleActionRow; tenantPropagation: TenantPropagationEntry[] },
      { moduleId: string; name: string; description?: string }
    >({
      query: (body) => ({
        url: TENANT_API.moduleActions,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<{
        moduleAction?: GlobalModuleActionRow;
        tenantPropagation?: TenantPropagationEntry[];
      }>) => {
        const data = unwrapResponse(response, {});
        return {
          moduleAction: data.moduleAction ?? ({
            id: '',
            name: '',
            moduleId: '',
          } as GlobalModuleActionRow),
          tenantPropagation: data.tenantPropagation ?? [],
        };
      },
    }),

    createGlobalPermission: builder.mutation<
      { permission: GlobalPermissionRow; tenantPropagation: TenantPropagationEntry[] },
      {
        code: string;
        moduleId: string;
        moduleActionId: string;
        label?: string;
        description?: string;
        scope?: string;
      }
    >({
      query: (body) => ({
        url: TENANT_API.permissions,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<{
        permission?: GlobalPermissionRow;
        tenantPropagation?: TenantPropagationEntry[];
      }>) => {
        const data = unwrapResponse(response, {});
        return {
          permission: data.permission ?? ({ id: '', code: '' } as GlobalPermissionRow),
          tenantPropagation: data.tenantPropagation ?? [],
        };
      },
    }),

    applyTenantPermissions: builder.mutation<
      {
        tenantId: string;
        tenantName: string;
        appliedCount: number;
        skippedCount: number;
        failedCount: number;
        items: Array<{
          permissionId: string;
          code: string;
          moduleName: string;
          moduleActionName: string;
          tenantPermissionId: string;
          tenantPermissionCreated: boolean;
          tenantDb: {
            moduleCreated: boolean;
            moduleActionCreated: boolean;
            permissionCreated: boolean;
            rolePermissionCreated: boolean;
            adminRoleFound: boolean;
            error?: string;
          };
        }>;
        unresolved: Array<{
          input: string;
          kind: string;
          reason: string;
        }>;
      },
      {
        tenantId: string;
        permissionIds?: string[];
        permissionCodes?: string[];
      }
    >({
      query: ({ tenantId, ...body }) => ({
        url: `api/tenants/${tenantId}/apply-permissions`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<any>) => unwrapResponse(response, {}),
    }),

    deleteGlobalModule: builder.mutation<
      any,
      { id: string; scope: 'global' | 'tenant'; tenantId?: string }
    >({
      query: ({ id, scope, tenantId }) => ({
        url: `${TENANT_API.modules}/${id}`,
        method: 'DELETE',
        params: { scope, tenantId },
      }),
      transformResponse: (response: ApiResponse<any>) => unwrapResponse(response, {}),
    }),

    deleteGlobalModuleAction: builder.mutation<
      any,
      { id: string; scope: 'global' | 'tenant'; tenantId?: string }
    >({
      query: ({ id, scope, tenantId }) => ({
        url: `${TENANT_API.moduleActions}/${id}`,
        method: 'DELETE',
        params: { scope, tenantId },
      }),
      transformResponse: (response: ApiResponse<any>) => unwrapResponse(response, {}),
    }),

    deleteGlobalPermission: builder.mutation<
      any,
      { id: string; scope: 'global' | 'tenant'; tenantId?: string }
    >({
      query: ({ id, scope, tenantId }) => ({
        url: `${TENANT_API.permissions}/${id}`,
        method: 'DELETE',
        params: { scope, tenantId },
      }),
      transformResponse: (response: ApiResponse<any>) => unwrapResponse(response, {}),
    }),
  }),
});

export const {
  useFilterTenantMutation,
  useCreateTenantMutation,
  useUpdateTenantMutation,
  useGetTenantDtlMutation,
  useDeleteTenantMutation,
  useUpdateLimitMutation,
  useGetBillingProfileQuery,
  useSaveBillingProfileMutation,
  useGetTenantPermissionAllowancesQuery,
  useSetTenantPermissionAllowanceMutation,
  useListGlobalModulesQuery,
  useListGlobalModuleActionsQuery,
  useListGlobalPermissionsQuery,
  useCreateGlobalModuleMutation,
  useCreateGlobalModuleActionMutation,
  useCreateGlobalPermissionMutation,
  useApplyTenantPermissionsMutation,
  useDeleteGlobalModuleMutation,
  useDeleteGlobalModuleActionMutation,
  useDeleteGlobalPermissionMutation,
} = tenantApi;
